import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, TrendingUp } from "lucide-react";

export default function TiersPanel() {
  const [tiers, setTiers] = useState([]);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/driver/tiers"), api.get("/admin/users"), api.get("/admin/bookings")]).then(([t, u, b]) => {
      setTiers(t.data);
      // compute jobs/driver
      const completed = b.data.filter((x) => x.status === "completed");
      const byDriver = {};
      completed.forEach((x) => {
        if (!x.driver_id) return;
        byDriver[x.driver_id] = (byDriver[x.driver_id] || 0) + 1;
      });
      const driversOnly = u.data.filter((x) => x.role === "driver").map((d) => {
        const jobs = byDriver[d.id] || 0;
        const tier = pickTier(t.data, jobs, 5.0);
        return { ...d, jobs, tier };
      });
      setDrivers(driversOnly);
    });
  }, []);

  return (
    <div className="space-y-6" data-testid="tiers-panel">
      <div className="grid md:grid-cols-3 gap-4">
        {tiers.map((t) => (
          <div key={t.id} className="border-2 border-asap-deep p-5 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${t.color}22, white)` }} data-testid={`tier-card-${t.id}`}>
            <div className="absolute -right-3 -top-3 opacity-20"><Trophy size={80}/></div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-asap-orange">{t.id === "gold" ? "Highest" : t.id === "silver" ? "Mid" : "Entry"}</div>
            <div className="font-display text-3xl uppercase text-asap-deep mt-1">{t.name}</div>
            <div className="mt-3 text-sm text-slate-700">
              <div><span className="font-bold">{t.min_jobs}+</span> completed jobs</div>
              <div><span className="font-bold">{t.min_rating > 0 ? t.min_rating : "—"}★</span> minimum rating</div>
            </div>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-asap-deep text-asap-yellow font-display"><TrendingUp size={14}/> {t.multiplier.toFixed(2)}× pay</div>
          </div>
        ))}
      </div>

      <div className="bg-white border-2 border-asap-deep p-5">
        <h3 className="font-display uppercase text-asap-deep mb-3">Driver leaderboard ({drivers.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="driver-leaderboard">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
              <tr><th className="py-2">Driver</th><th>Jobs</th><th>Tier</th><th>Multiplier</th><th>Online</th></tr>
            </thead>
            <tbody>
              {drivers.sort((a, b) => b.jobs - a.jobs).map((d) => (
                <tr key={d.id} className="border-b last:border-0">
                  <td className="py-2 font-bold text-asap-deep">{d.name}</td>
                  <td>{d.jobs}</td>
                  <td>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border font-bold border-asap-deep text-asap-deep">{d.tier.name}</span>
                  </td>
                  <td className="font-display text-asap-orange">{d.tier.multiplier.toFixed(2)}×</td>
                  <td>{d.online ? <span className="text-emerald-600 font-bold">●</span> : <span className="text-slate-300">●</span>}</td>
                </tr>
              ))}
              {drivers.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No drivers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function pickTier(tiers, jobs, rating) {
  for (const t of tiers) {
    if (jobs >= t.min_jobs && rating >= t.min_rating) return t;
  }
  return tiers[tiers.length - 1];
}
